import { useEffect, useState } from "react";
import { gql } from "__generated__/gql";
import { intl } from "LocaleWrapper";

import { useMutation } from "@apollo/client";
import { Table } from "@cofano/cck";
import WarningIcon from "@mui/icons-material/Warning";
import { Tooltip } from "@mui/material";

import ShipSelect from "view/ships/components/selects/ShipSelect";
import { usePermission } from "imports/Utils";

import { FINANCIALLY_UNREADY_MUTATION } from "./Invoicelines";

import "./declarations.scss";
import { DateUtils } from "utils/DateUtils";
import { TableId, useBackendTable } from "components/table";
import client from "../../apollo";
import { cantorPair } from "view/declarations/Declarations.jsx";
import DeclarationProgress from "./components/progress/DeclarationProgress";

const PurchaseCharters = ({ debtors, selectedCharterId, onSelect, onChange, window, updatePurchaseChartersRequestId, actuallyUpdatePurchaseCharterRequestId, setLoading, selectedSchedule }) => {

	const { isAdmin, isFinanceAdmin } = usePermission();
	const canOpen = isAdmin || isFinanceAdmin;

	const from = DateUtils.getTimestamp(window.from);
	const to = DateUtils.getTimestamp(window.to);

	const [ openDeclaration ] = useMutation(OPEN_DECLARATION_MUTATION, {
		onCompleted: () => {
			actuallyUpdatePurchaseCharterRequestId();
			onSelect({ id: selectedCharterId });
		},
	});

	const [ financiallyUnreadyDeclaration ] = useMutation(FINANCIALLY_UNREADY_MUTATION, {
		onCompleted: () => {
			actuallyUpdatePurchaseCharterRequestId();
			onSelect({ id: selectedCharterId });
		},
	});

	const { tableProps, refetch } = useBackendTable({
		id: TableId.DeclarationPurchaseCharters,
		onChange: async (pagination, sort, filter, callback) => {
			setLoading(true);

			try {
				const result = await client.query({
					query: query,
					variables: {
						search: {
							timeWindow: {
								from: from,
								to: to,
							},
							shipIds: filter.ship?.id ? [ filter.ship.id ] : [],
							debtorIds: debtors.length > 0 ? debtors.map(debtor => debtor.id) : [],
							scheduleIds: selectedSchedule?.id ? [ selectedSchedule.id ] : [],
						},
						pagination: {
							...pagination,
							sortList: sort,
						},
						from: from,
						to: to,
					},
				});

				callback({
					content: result.data.purchaseCharters.content.map(it => ({
						...it,
						id: cantorPair(it.purchaseCharter.id, it.ship.id),
					})),
					totalResults: result.data.purchaseCharters.totalElements,
				});

				onChange(result.data.purchaseCharters.content);
				setLoading(false);
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});

				setLoading(false);
			}
		},
	});


	useEffect(() => {
		refetch();
	}, [ from, to, updatePurchaseChartersRequestId, debtors, selectedSchedule ]);

	return (
		<Table
			{...tableProps}
			key={updatePurchaseChartersRequestId}
			onRowClick={(row) => {
				if (onSelect) {
					onSelect(row.purchaseCharter);
				}
			}}
			highlightRow={row => {
				if (selectedCharterId && (row.purchaseCharter.id === selectedCharterId)) {
					return "HIGHLIGHT";
				} else {
					return "NONE";
				}
			}}
			columns={[ {
				id: "ship",
				title: intl.formatMessage({ id: "SHIP" }),
				render: (row) => {
					return row.ship.name || "";
				},
				filterComponent: (value, onFilterChange) => <ShipSelect label={intl.formatMessage({ id: "SHIP" })} value={value} onChange={onFilterChange} />,
				width: 150,
			}, {
				id: "indicators",
				render: (row) => {
					return row.purchaseCharter.voyages?.length === 0 &&
						<Tooltip title={intl.formatMessage({ id: "NO_VOYAGES_FOUND" })}>
							<WarningIcon fontSize="small" color="error" />
						</Tooltip>;
				},
				sortable: false,
				width: 30,
			}, {
				id: "financiallyReadiedOn",
				width: 50,
				title: intl.formatMessage({ id: "FINANCIALLY_READY" }),
				render: (row) => {
					if (row.purchaseCharter.declarations?.[0]?.financiallyReadiedOn) {
						return (
							<OpenDeclarationSwitch
								canOpen={canOpen && !row.purchaseCharter.declarations[0].closedOn}
								declaration={row.purchaseCharter.declarations[0]}
								financial
								callback={financiallyUnreadyDeclaration}
							/>
						);
					} else {
						return intl.formatMessage({ id: "NO" });
					}
				},
			}, {
				id: "filled",
				title: intl.formatMessage({ id: "PURCHASE_CHARTER_FILLED" }),
				width: 60,
				render: row => {
					if (row.purchaseCharter.declarations?.[0]) {
						if (row.purchaseCharter.declarations[0].closedOn) {
							return (
								<OpenDeclarationSwitch
									canOpen={canOpen}
									declaration={row.purchaseCharter.declarations[0]}
									callback={openDeclaration} />
							);
						}

						const checks = row.purchaseCharter.declarations[0].progress?.progressChecks || [];

						return (
							<DeclarationProgress
								checkedCount={checks.filter(c => c.checked).length}
								totalCount={checks.length}
								value={row.purchaseCharter.declarations[0].progress?.percentage ?? 0}
							/>
						);
					} else {
						return (
							<DeclarationProgress
								checkedCount={0}
								totalCount={0}
								value={0}
							/>
						);
					}
				},
			} ]} />
	);
};

export const OpenDeclarationSwitch = ({ declaration, callback, canOpen, financial }) => {

	const [ mousedOver, setMousedOver ] = useState(false);
	const [ hadFirstClick, setHadFirstClick ] = useState(false);

	const onClick = (e) => {
		if (!canOpen) {
			return;
		}
		e.preventDefault();
		e.stopPropagation();
		if (hadFirstClick) {
			callback({ variables: { id: declaration.id } });
		} else {
			setHadFirstClick(true);
		}
	};

	const onMouseLeave = () => {
		if (!canOpen) {
			return;
		}
		setMousedOver(false);
		setHadFirstClick(false);
	};

	const text = hadFirstClick ? intl.formatMessage({ id: "CONFIRM" }) : mousedOver ?
		`${intl.formatMessage({ id: "REOPEN" })}?` : financial ? intl.formatMessage({ id: "FINANCIALLY_READY" }) :
			intl.formatMessage({ id: "CLOSED" });

	return <span
		className={mousedOver ? "moused-over" : ""}
		onMouseEnter={() => {
			if (canOpen) {
				setMousedOver(true);
			}
		}}
		onClick={onClick}
		onMouseLeave={onMouseLeave}>{text}</span>;

};

export const OPEN_DECLARATION_MUTATION = gql(`#graphql
	mutation open($id: Long) {
		open(id: $id) {
			id
		}
	}
`);

const query = gql(`#graphql
	query purchaseCharters_purchaseCharters($search: PurchaseCharterSearch!, $pagination: Pagination!, $from: Instant, $to: Instant) {
		purchaseCharters(search: $search, pagination: $pagination) {
			totalElements
			content {
				ship {
					id
					name
				}
				purchaseCharter {
					id
					type
					declarations(from: $from, to: $to) {
						id
						declarationValues {
							id
						}
						closedOn
						financiallyReadiedOn
						invoiceLines {
							id
							invoiceReference
							description
						}
						progress {
							percentage
							progressChecks {
								description
								checked
							}
						}
					}
					voyages(from: $from, to: $to, excludeEmpty: true) {
						id
					}
				}
			}
		}
	}
`);

export default PurchaseCharters;
